import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from 'services/CPPUtils';
import './style.scss';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import Tooltip2 from 'components/Common/Tooltip2';
import { formatLmpDisplay } from './formatLmpLabel';

const DEFAULT_OBJECT = {};

const CPPInformationPanel = ({ patient, information = DEFAULT_OBJECT }) => {
  const role = useAppSelector((state) => state.authentication.role);
  const allowEdit = role === 'physician' || role === 'admin';

  return (
    <div className="cpp-small-box list-element information">
      <div className="top-info row">
        <Icon src="circle-info" width="20px" height="20px" style={{ color: '#838395' }} />
        <span className="small-title uppercase">Medical Info</span>
      </div>
      <div className="list-content">
        <div className="list">
          <BloodTypeInfo patient={patient} allowEdit={allowEdit} information={information} />
          <LMPInfo patient={patient} />
        </div>
      </div>
    </div>
  );
};

// Read-only datum: the LMP the patient self-reports in the Care& menstrual
// tracker (most recent non-spotting bleed start). Always labelled
// "patient-tracked" so a clinician never reads it as a charted LMP, and is not
// editable from here. Source data lives in healthRecords.byId[id].lmp, fetched
// alongside the other CPP loads.
const LMPInfo = ({ patient }) => {
  const lmp = useAppSelector((state) => state.healthRecords.byId?.[patient?.id]?.lmp);

  // LMP is a menstrual datum — don't surface it for male patients (the server
  // also returns no LMP for them; this keeps the row out of the Medical Info box
  // entirely rather than rendering "LMP: N/A").
  if (patient?.gender === 'Male') return null;

  const { inline, details } = formatLmpDisplay(lmp);

  // Headline (the date) stays inline; the "patient-tracked" / "last entry"
  // qualifiers move into the hover tooltip so the narrow panel doesn't overflow.
  const tooltip = details.length ? (
    <div>
      <span>
        <b>LMP</b>
      </span>
      <br />
      {inline}
      {details.map((detail) => (
        <React.Fragment key={detail}>
          <br />
          {detail}
        </React.Fragment>
      ))}
    </div>
  ) : null;

  return (
    <li className="lmp inline-block">
      <Tooltip2 tooltip={tooltip}>
        {(register) => (
          <span className="tooltip-wrapper list-element truncate animated fadeIn" {...register}>
            LMP: {inline}
          </span>
        )}
      </Tooltip2>
    </li>
  );
};

const BloodTypeInfo = ({ patient, allowEdit, information }) => {
  const { blood_type } = information;

  const [showModal, setShowModal] = useState(false);

  let className = 'blood-type inline-block';
  if (allowEdit) className += ' cursor-pointer';

  return (
    <li className={className}>
      <span
        className="tooltip-wrapper list-element truncate animated fadeIn"
        onClick={allowEdit ? () => setShowModal(true) : undefined}
      >
        Blood type: {blood_type || 'N/A'}
      </span>
      {showModal && (
        <DefaultModal
          title="Edit Blood Type"
          wrap={true}
          padding={true}
          className="small-modal"
          content={
            <EditBloodType
              patient={patient}
              onSuccess={() => setShowModal(false)}
              information={information}
            />
          }
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </li>
  );
};

const EditBloodType = ({ patient, onSuccess, information }) => {
  const dispatch = useAppDispatch();

  const { id } = patient;
  const { blood_type } = information;

  const [modifiedBloodType, setModifiedBloodType] = useState(blood_type);
  const [loading, setLoading] = useState(false);

  async function onChangeBloodType() {
    setLoading(true);
    const newInformation = { ...information, blood_type: modifiedBloodType };
    const result = await dispatch(
      api.HealthRecords.updateHealthRecordsInformation({
        patientId: id,
        healthRecordsInformation: newInformation,
      }),
      { errorTitle: 'Update Blood Type' },
    );
    setLoading(true); // TODO MIGUEL migration <- found this, should be false?
    if (result && onSuccess) onSuccess(false);
  }

  return (
    <div className="edit-blood-type flex flex-col">
      <DefaultSelectField
        label="Blood Type"
        name="blood_type"
        value={modifiedBloodType}
        onChange={(event) => setModifiedBloodType(event.target.value)}
        inputs={CPPUtils.getBloodTypes()}
      />
      <DefaultButton
        name="Save Changes"
        className="blue mt-2"
        style={{ width: '250px', marginTop: '20px' }}
        onClick={onChangeBloodType}
        loading={loading}
      />
    </div>
  );
};

CPPInformationPanel.propTypes = {
  patient: PropTypes.object,
  information: PropTypes.object,
};

export default CPPInformationPanel;
